/**
 * ReadyPickupPopup — Home-screen modal that surfaces a customer's pickup
 * QR the moment they open the app while an order is waiting at the
 * counter (Business Rule #17).
 *
 * Self-contained: on mount it hits ``GET /api/user/active-pickup`` and
 * renders nothing if there's no eligible order. When an order is found,
 * it overlays a Dialog with the same QR + Rista invoice + 4-char ref
 * that the order page shows.
 *
 * Dismissal is sticky per-order via ``sessionStorage`` so closing the
 * popup doesn't immediately re-pop on the next render or navigation —
 * but reopening the app fresh (new session) will surface it again until
 * the order is collected. The OrderTracking / OrderConfirmation pages
 * keep their inline QR card intact regardless.
 */
import { useEffect, useState, useCallback } from 'react';
import { reportHomePopup } from '../utils/homePopups';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScanLine, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_LEAD_SECONDS = 30;

function sessionDismissedKey(orderId) {
  return `pbc_pickup_popup_dismissed_${orderId}`;
}

export default function ReadyPickupPopup({ token }) {
  const navigate = useNavigate();
  const [activeOrder, setActiveOrder] = useState(null);
  const [qr, setQr] = useState(null);
  const [open, setOpen] = useState(false);

  // One pop-up per app open on Home (Business Rule #64) — the launch pop-up stands aside.
  useEffect(() => {
    reportHomePopup('ready_pickup', open);
    return () => reportHomePopup('ready_pickup', false);
  }, [open]);

  // ── Step 1: poll for an active ready pickup order ──
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const check = async () => {
      try {
        const resp = await axios.get(`${API}/user/active-pickup`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const order = resp.data;
        if (!order) {
          setActiveOrder(null);
          setOpen(false);
          return;
        }
        if (sessionStorage.getItem(sessionDismissedKey(order.id))) {
          // Honour an explicit close for this session — but keep the
          // order around so the QR card on the order tracking page is
          // still reachable.
          setActiveOrder(order);
          setOpen(false);
          return;
        }
        setActiveOrder(order);
        setOpen(true);
      } catch (_) {
        // Silent — popup is non-essential.
      }
    };
    check();
    // Re-check every 60s while the home page is mounted so the popup
    // appears even if the user is sitting on home when the order flips
    // to ready (no WebSocket on this page).
    const interval = setInterval(check, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  // ── Step 2: fetch a signed QR token for the active order ──
  const fetchQr = useCallback(async () => {
    if (!activeOrder || !token) return;
    try {
      const resp = await axios.get(
        `${API}/orders/${activeOrder.id}/pickup-qr`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setQr(resp.data);
    } catch (_) {
      // Backend refused (order moved past ready, collected, etc.) — hide.
      setQr(null);
      setOpen(false);
    }
  }, [activeOrder, token]);

  useEffect(() => {
    if (open && activeOrder) fetchQr();
  }, [open, activeOrder, fetchQr]);

  // Auto-refresh just before token expiry while the modal is open.
  useEffect(() => {
    if (!open || !qr?.expires_in_seconds) return undefined;
    const ms = Math.max(15_000, (qr.expires_in_seconds - REFRESH_LEAD_SECONDS) * 1000);
    const t = setTimeout(fetchQr, ms);
    return () => clearTimeout(t);
  }, [open, qr, fetchQr]);

  const handleClose = (next) => {
    setOpen(next);
    if (!next && activeOrder) {
      sessionStorage.setItem(sessionDismissedKey(activeOrder.id), '1');
    }
  };

  if (!activeOrder) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-sm p-0 overflow-hidden gap-0 border-primary/20"
        data-testid="ready-pickup-popup"
      >
        {/* Accessible labels for screen readers — DialogContent renders these visually if not visually-hidden, so use sr-only here since the card body already shows the headline. */}
        <DialogTitle className="sr-only">Order ready for pickup</DialogTitle>
        <DialogDescription className="sr-only">
          Show this QR code at the counter to collect your order.
        </DialogDescription>

        <div className="bg-gradient-to-br from-accent/15 via-background to-accent/5">
          <div className="px-5 pt-5 pb-3 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold leading-tight">Your order is ready!</p>
              <p className="text-xs text-muted-foreground">
                Show this QR at the counter
                {activeOrder.branch_name ? ` · ${activeOrder.branch_name}` : ''}
              </p>
            </div>
          </div>

          {/* Business Rule #25: Reference ID first, Invoice # secondary. */}
          <div className="px-5 grid grid-cols-2 gap-2 mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Reference ID</p>
              <p className="text-xl font-bold font-mono tabular-nums tracking-tight">
                #{activeOrder.reference_id || (activeOrder.id || '').slice(0, 4)}
              </p>
            </div>
            {activeOrder.rista_invoice_number && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Invoice #</p>
                <p className="text-base font-semibold font-mono tabular-nums text-muted-foreground">
                  {activeOrder.rista_invoice_number}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-center pb-4">
            <div className="bg-white p-3 rounded-xl shadow-sm border border-border">
              {qr ? (
                <QRCodeSVG
                  value={qr.token}
                  size={200}
                  level="M"
                  includeMargin={false}
                  bgColor="#FFFFFF"
                  fgColor="#1A1208"
                />
              ) : (
                <div className="w-[200px] h-[200px] flex items-center justify-center text-muted-foreground text-xs">
                  Loading QR…
                </div>
              )}
            </div>
          </div>

          <div className="px-5 pb-5 flex flex-col gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary"
              onClick={() => {
                handleClose(false);
                navigate(`/orders/${activeOrder.id}`);
              }}
            >
              View order details
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
